/**
 * ConversationTranscript — the « Lecture de la conversation » block of the Run view (§4.5.4).
 *
 * Design: `01-lecture-dabord.html`. Three columns, and the middle one is a DEVICE.
 *
 *   gouttière (84px) │ APPAREIL (13 + 620 + 13) │ rail de mesure (300px)
 *
 * The device is "larger than life" (×1.6 a real phone): a 620px screen with 30px screen
 * margins leaves a 560px measure, set in Source Serif 4 at 16.5px/1.7 — about 65 to 70
 * characters a line, which is a reading measure instead of a 42-character phone column.
 * The chassis is painted by an `aria-hidden` overlay grid behind column 2 so ONE frame
 * runs behind many turn rows; its radius sits on the BORDER box with a uniform 13px
 * border, so the screen's 39px inner radius is derived by construction and never
 * hand-computed. One soft shadow, no gloss, no gradient, no drawn notch.
 *
 * Inside the screen = the conversation, unretouched: what the persona asked, what the
 * engine answered. Outside the screen = Traaker measurement: the turn number and the
 * MOMENT of the conversation on the left, the measured signals on the right. The only
 * chrome that survives carries real data — the status row (engine, zone, run time) and
 * the foot hairline (how the conversation ended). No tab bar, no action bar, no fake
 * input: an inert control is a lie about an export.
 *
 * The drop-out is the strongest moment in the document and the device is how it is told:
 * the frame ends flat above the drop-out turn and resumes flat below it, so the
 * full-bleed band physically SLICES the screen.
 *
 * Engine voice is carried by two signals only — the serif prose (reserved to engine
 * output) and the right-aligned, neutrally-tinted question bubble. The bubble is never
 * violet: violet means « nous », and the persona is not us.
 *
 * The annotation rail carries MEASURED signals only (state, rank, alignment, sources,
 * competitors, observed intent) — never generated prose, and never the judge's
 * `recommendation`, which is advice.
 *
 * Security: engine prose and URLs are attacker-chosen web text. Everything is rendered
 * as React text nodes (no dangerouslySetInnerHTML); a link is emitted only for an
 * http(s) URL, always with rel="noopener noreferrer".
 */

import { useState } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  TARGET_CATEGORIES,
  type ConversationTurn,
  type ConversationTurnMatch,
  type TargetHighlight,
  type TargetSourceHighlight,
} from '@traaker/shared';
import { HighlightedResponse } from '../common/HighlightedResponse';
import { sourceHref, isGroundingWrapper } from '../../utils/sourceUrl';
import { EngineLogo, ENGINE_META, engineKey } from '../brand/EngineLogo';
import DomainFavicon from '../competitors/DomainFavicon';

interface Props {
  turns: ConversationTurn[];
  /** Resolves each matched marque's highlight colour (D45). */
  brandId?: string;
  brandName: string;
  /** First turn the brand is absent from after having been cited; `null` otherwise. */
  dropOutTurn: number | null;
  /** Brands the LLM named that are neither ours nor tracked competitors. */
  untrackedBrands: string[];
  /** Already added to the tracked competitors during this session. */
  addedBrands: string[];
  onAddCompetitor?: (label: string) => void;
  /** Resolved domain for each untracked brand, keyed by LOWERCASED brand name. */
  resolvedDomains?: Map<string, { name: string; domain: string }>;
  /** Engine that ran this conversation — shown in the device status row. */
  engine?: string;
  /** Zone label (e.g. "France") — shown in the device status row. */
  zoneLabel?: string;
  /** ISO-3166-1 alpha-2 country code for the flag emoji (e.g. "FR"). */
  countryCode?: string;
}

// ---------------------------------------------------------------------------
// Device metrics. Changing one of these three changes the reading measure.
// ---------------------------------------------------------------------------

const SERIF = '"Source Serif 4", Georgia, "Times New Roman", serif';
const TIGHT = '"Inter Tight", Inter, -apple-system, "Segoe UI", sans-serif';
/** Even on all four sides — a uniform border is what derives the inner radius. */
const BEZEL = 13;
/** On the BORDER box. Inner radius = 52 - 13 = 39px, by construction. */
const RADIUS = 52;
/** Screen margin inside the bezel; 620 - 2×30 = 560px measure. */
const SCREEN_MARGIN = 30;
/** Content inset from the frame's outer edge. */
const INSET = BEZEL + SCREEN_MARGIN;

/** gouttière | appareil | rail. The one grid every row of this block aligns to. */
const GRID: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    md: '68px minmax(0, 1fr) 236px',
    lg: '84px 646px 300px',
  },
  columnGap: { md: '22px', lg: '40px' },
  maxWidth: 1110,
  mx: 'auto',
  alignItems: 'start',
};

/**
 * The three validated violet steps for an ordinal ramp (ΔL ≥ 0.06, light end 2.72:1).
 * Rank 4 and beyond get the bare digit on neutral — no fourth hue is invented.
 */
const RANK_RAMP = ['#4C1D95', '#7C3AED', '#A78BFA'];
const RANK_NEUTRAL = '#E6E7EB';

function rankChipSx(rank: number): SxProps<Theme> {
  const bg = RANK_RAMP[rank - 1];
  return {
    flex: '0 0 22px',
    height: 20,
    borderRadius: '5px',
    fontSize: '11.5px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontVariantNumeric: 'tabular-nums',
    bgcolor: bg ?? RANK_NEUTRAL,
    // The light end of the ramp needs dark ink; the two dark steps need white.
    color: bg ? (rank === 3 ? '#1B0B33' : '#FFFFFF') : 'text.primary',
  };
}

function flagEmoji(cc: string): string {
  return [...cc.toUpperCase()].map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0))).join('');
}

/** Extract the displayable hostname from a raw URL or domain string. */
function displayDomain(raw: string): string {
  if (!raw) return raw;
  try {
    const url = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    return url.hostname.replace(/^www\./, '');
  } catch {
    // Not a URL — return as-is (might already be a short label)
    return raw.length > 60 ? raw.slice(0, 57) + '…' : raw;
  }
}

/**
 * The path half of a source line. Empty for a Gemini grounding-redirect wrapper: its
 * URL is an opaque click-tracker and must never be shown.
 */
function displayPath(raw: string): string {
  if (!raw || isGroundingWrapper(raw)) return '';
  try {
    const url = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    const p = url.pathname + url.search;
    return p === '/' ? '' : p;
  } catch {
    return '';
  }
}

/**
 * A turn's `targetMatches` → the two highlight lists `HighlightedResponse` consumes.
 *
 * A `pair` match becomes a CONDITIONAL highlight, exactly as Brand Perception's
 * `targets_shadow._highlights` builds one: the row stores the halves glued as `'a|b'`,
 * so split them and ship half `a` as `value` with half `b` as `pairWith` + the
 * matcher's `window`. `targetTermsFrom` then re-runs `isPairSatisfied` per occurrence.
 * Half `b` gets no entry of its own — it is a marque alias by construction, already
 * painted by the marque's own signal in the same colour (D45).
 *
 * A pair with no `window` is DROPPED (rows written before 2026-09-25): the gate cannot
 * be evaluated, and an UNGATED pair half is exactly what once painted a competitor's
 * address in the client's brand colour (targets.md §11.1). Failing closed loses a
 * highlight; the alternative invents one. The match itself is untouched either way, so
 * the turn still reads as cited.
 */
function highlightsOf(matches: ConversationTurnMatch[] | undefined): {
  prose: TargetHighlight[];
  sources: TargetSourceHighlight[];
} {
  const prose: TargetHighlight[] = [];
  const sources: TargetSourceHighlight[] = [];
  for (const m of matches ?? []) {
    if (m.via === 'source') {
      if (m.sourceIndex !== undefined) {
        sources.push({ sourceIndex: m.sourceIndex, targetId: m.targetId, rootMarqueId: m.rootMarqueId });
      }
      continue;
    }
    if (m.kind === 'pair') {
      // Split from the RIGHT: half `b` is a marque alias and never contains '|',
      // while half `a` is free text that could.
      const cut = m.value.lastIndexOf('|');
      if (cut <= 0) continue;
      const halfA = m.value.slice(0, cut);
      const halfB = m.value.slice(cut + 1);
      if (!halfA || !halfB || !m.window || m.window <= 0) continue;
      prose.push({
        value: halfA,
        pairWith: halfB,
        window: m.window,
        targetId: m.targetId,
        rootMarqueId: m.rootMarqueId,
      });
      continue;
    }
    prose.push({ value: m.value, targetId: m.targetId, rootMarqueId: m.rootMarqueId });
  }
  return { prose, sources };
}

export function stageOf(turn: ConversationTurn): 'exploration' | 'evaluation' | 'decision' {
  if (turn.stage) return turn.stage;
  if (turn.turn <= 1) return 'exploration';
  if (turn.turn <= 4) return 'evaluation';
  return 'decision';
}

// ---------------------------------------------------------------------------
// Observed intent — the OTHER axis. Never mixed with `stage`.
// ---------------------------------------------------------------------------

/**
 * Funnel depth of an observed intent: 1 = haut, 2 = milieu, 3 = bas. `null` for every
 * other key of the live taxonomy (`intra_compare`, `authority`, `transact`,
 * `troubleshoot`, `other`) — those are real intents that are NOT one of the three
 * depths, and folding them into one would invent a funnel position.
 *
 * Deliberately NOT rendered as a progress bar, staircase, arrow, gradient or cumulative
 * fill: `observedIntent` is measured per turn and can move in ANY direction, including
 * back up. `stage` is the axis that is linear, and it lives in the left gutter in plain
 * grey. Any visual here that implies « turn 1 → turn 6 = top → bottom of funnel » is a
 * claim the data does not make.
 */
function depthOf(intent: string | undefined): 1 | 2 | 3 | null {
  if (intent === 'learn') return 1;
  if (intent === 'discover') return 2;
  if (intent === 'compare') return 3;
  return null;
}

// ---------------------------------------------------------------------------
// Rail state — the eight readings a turn can have.
// ---------------------------------------------------------------------------

type RailState = 'fail' | 'exit' | 'induced' | 'eliminated' | 'source' | 'weak' | 'in' | 'out';

function railStateOf(turn: ConversationTurn, isDropOut: boolean): RailState {
  if (turn.turnStatus === 'failed') return 'fail';
  // The drop-out outranks `induced` so the rail agrees with the band above it; an
  // induced turn still gets its own « ce tour ne compte pas » flag either way.
  if (isDropOut) return 'exit';
  if (turn.induced) return 'induced';
  if (turn.eliminated === true) return 'eliminated';
  if (turn.cited && turn.brandIsSource) return 'source';
  if (turn.cited && typeof turn.narrativeAlignment === 'number' && turn.narrativeAlignment < 60) return 'weak';
  if (turn.cited) return 'in';
  return 'out';
}

/** `spine: null` = the neutral dotted spine (nothing measured to colour). */
const RAIL_STATE: Record<RailState, { dot: string; ink: string; spine: string | null; glyph?: string }> = {
  source: { dot: 'success.main', ink: 'text.primary', spine: 'success.main' },
  in: { dot: 'success.main', ink: 'text.primary', spine: 'success.main' },
  weak: { dot: 'warning.main', ink: 'text.primary', spine: 'warning.main' },
  eliminated: { dot: 'error.main', ink: 'error.dark', spine: 'error.main', glyph: '×' },
  exit: { dot: 'error.main', ink: 'error.dark', spine: 'error.main', glyph: '×' },
  out: { dot: 'text.disabled', ink: 'text.secondary', spine: null },
  fail: { dot: 'text.disabled', ink: 'text.secondary', spine: null },
  induced: { dot: 'text.disabled', ink: 'text.secondary', spine: null },
};

const STATE_KEY: Record<RailState, string> = {
  source: 'Source',
  in: 'In',
  weak: 'Weak',
  eliminated: 'Eliminated',
  exit: 'Exit',
  out: 'Out',
  fail: 'Fail',
  induced: 'Induced',
};

/**
 * One sentence for WHY the brand drops out at this turn.
 *
 * Derived from signals already MEASURED on the turn (and on the one before it, for the
 * two that are a change rather than a state) — never generated prose, and never the
 * judge's `recommendation`, which is a suggestion, not an observation. Same rule as the
 * annotation rail itself.
 *
 * The ladder returns the most specific observation that holds, so a turn that both lost
 * its source and names a rival reads as "the answer switched to X" — the actionable one.
 * The last rung says nothing happened that we can see, rather than inventing a cause:
 * a drop with no measured signal is a real outcome and must read as one.
 */
function dropReason(
  turn: ConversationTurn,
  prev: ConversationTurn | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (turn.eliminated === true) return t('conversations.annoDropWhyEliminated');

  // Tracked competitors first (Layer A, deterministic); the judge's brand list is the
  // fallback so a rival we do not track yet still names itself as the reason.
  const rivals = ((turn.competitorsMentioned ?? []).length > 0
    ? turn.competitorsMentioned
    : (turn.otherBrandsCited ?? [])
  ).filter(Boolean);
  if (rivals.length > 0) {
    // Two names, no "+N" — the « N concurrents cités » row on the same rail already
    // carries the count, and a second count here would just contradict it on rounding.
    return t('conversations.annoDropWhyCompetitors', { brands: rivals.slice(0, 2).join(', ') });
  }

  if (prev?.brandIsSource && !turn.brandIsSource && (turn.sources?.length ?? 0) > 0) {
    return t('conversations.annoDropWhyLostSource');
  }
  if ((turn.sources?.length ?? 0) === 0 && (prev?.sources?.length ?? 0) > 0) {
    return t('conversations.annoDropWhyNoSources');
  }
  return t('conversations.annoDropWhyUnknown');
}

// How many sources to show before collapsing. The [n] markers are still accurate because
// the list is positional: we show first N entries and offer the rest behind a toggle.
const SOURCES_VISIBLE = 5;

/**
 * The turn's source list, strictly POSITIONAL: `sources[i]` is the label, `sourceLinks[i]`
 * the URL, and both are index-aligned to the inline `[n]` markers in the prose. Built with
 * `map` and never `filter` — dropping or reordering one entry re-points every marker after
 * it. The tail is collapsed, never removed.
 */
function TurnSources({
  sources,
  sourceLinks,
  targetMatches,
}: {
  sources: string[];
  sourceLinks?: string[];
  targetMatches?: ConversationTurnMatch[];
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (sources.length === 0) return null;

  const visible = expanded ? sources : sources.slice(0, SOURCES_VISIBLE);
  const hidden = sources.length - SOURCES_VISIBLE;

  return (
    <Box sx={{ mt: '22px', pt: '16px', borderTop: '1px solid', borderColor: 'divider' }}>
      <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, fontSize: '13px', color: 'text.secondary' }}>
        {visible.map((s, i) => {
          const link = (sourceLinks ?? [])[i] || '';
          const href = sourceHref(link || s);
          const matched = (targetMatches ?? []).some((mm) => mm.via === 'source' && mm.sourceIndex === i);
          // For Gemini / Google: `s` (from turn.sources) is the clean domain label
          // (e.g. "clarins.fr"); sourceLinks[i] is the vertexaisearch redirect wrapper.
          // Prefer s for display; prefer sourceLinks for the clickable href.
          const label = displayDomain(s || link) || s;
          const path = displayPath(s.includes('/') ? s : link);
          return (
            <Box
              component="li"
              key={i}
              sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '5px', minWidth: 0 }}
            >
              <Box component="span" sx={{ flex: '0 0 17px', fontSize: '10.5px', fontWeight: 700, color: 'text.disabled', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {i + 1}
              </Box>
              <DomainFavicon domain={label} name={label} size={17} />
              {href ? (
                <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ color: 'text.primary', fontWeight: 500, textDecoration: 'none', flex: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  {label}
                </Link>
              ) : (
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 500, flex: 'none' }}>{label}</Box>
              )}
              {path && (
                <Box component="span" sx={{ color: 'text.disabled', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  {path}
                </Box>
              )}
              {matched && (
                <Box component="span" sx={{ ml: 'auto', flex: 'none', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'primary.dark', bgcolor: (th) => alpha(th.palette.primary.main, 0.08), borderRadius: '4px', px: '5px', py: '2px' }}>
                  {t('conversations.trV2.srcMine')}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      {hidden > 0 && (
        <Box
          component="button"
          type="button"
          onClick={() => setExpanded(!expanded)}
          sx={{ all: 'unset', cursor: 'pointer', display: 'block', mt: '4px', fontSize: '12.5px', color: 'primary.dark', '&:hover': { textDecoration: 'underline' } }}
        >
          {expanded ? t('conversations.runSourcesCollapse') : t('conversations.trV2.srcMore', { count: hidden })}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Small rail pieces
// ---------------------------------------------------------------------------

const KICKER_SX: SxProps<Theme> = {
  fontSize: '0.625rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.disabled',
};

const CARD_SX: SxProps<Theme> = {
  mt: '14px',
  p: '11px 12px',
  bgcolor: 'background.paper',
  borderRadius: '12px',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
};

function RailRows({ rows }: { rows: [string, string, boolean][] }) {
  if (rows.length === 0) return null;
  return (
    <Box component="dl" sx={{ m: '14px 0 0', fontSize: '12.5px', color: 'text.secondary' }}>
      {rows.map(([label, value, na]) => (
        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px', py: '3.5px', borderTop: '1px solid', borderColor: 'divider' }}>
          <Box component="dt" sx={{ color: 'text.disabled' }}>{label}</Box>
          <Box component="dd" sx={{ m: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: na ? 'text.disabled' : 'text.primary', fontWeight: na ? 400 : 500 }}>
            {value}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function RailFlag({ title, body, hatched }: { title: string; body: string; hatched?: boolean }) {
  return (
    <Box
      sx={{
        mt: '13px',
        fontSize: '12px',
        lineHeight: 1.42,
        color: 'text.secondary',
        ...(hatched
          ? {
              background: 'repeating-linear-gradient(135deg, #F3F2F8 0 4px, #E9E8F0 4px 8px)',
              borderRadius: '10px',
              p: '10px 12px',
            }
          : null),
      }}
    >
      <Box component="b" sx={{ display: 'block', color: 'text.primary', fontSize: '12.5px' }}>{title}</Box>
      {body}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// The chassis — one continuous frame painted BEHIND column 2.
// ---------------------------------------------------------------------------

function Chassis({ edge }: { edge: 'full' | 'top' | 'bottom' }) {
  return (
    <Box
      aria-hidden
      sx={{
        ...GRID,
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        alignItems: 'stretch',
        display: { xs: 'none', md: 'grid' },
      }}
    >
      <Box />
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'background.paper',
          border: `${BEZEL}px solid`,
          // `secondary.main` IS the bezel colour (#0B0B0F).
          borderColor: 'secondary.main',
          // The radius is set on the BORDER box; with a uniform 13px border the screen's
          // 39px inner radius falls out of the nesting. Never hand-compute it.
          borderRadius:
            edge === 'top'
              ? `${RADIUS}px ${RADIUS}px 0 0`
              : edge === 'bottom'
                ? `0 0 ${RADIUS}px ${RADIUS}px`
                : `${RADIUS}px`,
          // A sliced frame ends FLAT: the screen is cut, not closed.
          ...(edge === 'top' ? { borderBottomWidth: 0 } : null),
          ...(edge === 'bottom' ? { borderTopWidth: 0 } : null),
          boxShadow: '0 34px 70px -26px rgba(11, 11, 15, 0.30), 0 3px 10px rgba(11, 11, 15, 0.07)',
          // The island: small, dark, centred, with nothing underneath it.
          ...(edge !== 'bottom'
            ? {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 11,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 132,
                  height: 20,
                  borderRadius: '10px',
                  bgcolor: 'secondary.main',
                },
              }
            : null),
        }}
      />
      <Box />
    </Box>
  );
}

export function ConversationTranscript({
  turns,
  brandId,
  brandName,
  dropOutTurn,
  untrackedBrands,
  addedBrands,
  onAddCompetitor,
  resolvedDomains,
  engine,
  zoneLabel,
  countryCode,
}: Props) {
  const { t, i18n } = useTranslation();

  if (turns.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{t('conversations.transcriptEmpty')}</Typography>;
  }

  /** The turn an untracked brand first appears on — one « marque non suivie » card, not six. */
  const firstSeen = new Map<string, number>();
  for (const turn of turns) {
    for (const b of turn.otherBrandsCited ?? []) {
      if (untrackedBrands.includes(b) && !firstSeen.has(b)) firstSeen.set(b, turn.turn);
    }
  }

  /** Turn by number — `dropReason` needs the turn before the drop to read a CHANGE. */
  const byTurn = new Map(turns.map((x) => [x.turn, x]));

  /**
   * The band only slices the frame when there IS something on both sides of it; a
   * drop-out on the very first turn renders the band above a single intact frame.
   */
  const sliceAt =
    dropOutTurn !== null && turns.some((x) => x.turn < dropOutTurn) && turns.some((x) => x.turn >= dropOutTurn)
      ? dropOutTurn
      : null;
  const bandAbove = dropOutTurn !== null && sliceAt === null;

  const head = sliceAt === null ? turns : turns.filter((x) => x.turn < sliceAt);
  const tail = sliceAt === null ? [] : turns.filter((x) => x.turn >= sliceAt);

  const runDate = (() => {
    const d = new Date(turns[0].createdAt);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(i18n.language, {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  })();

  // ---- one turn row ----
  function renderTurn(turn: ConversationTurn, indexInSegment: number, isLastOfAll: boolean) {
    const sources = turn.sources ?? [];
    const highlights = highlightsOf(turn.targetMatches);
    const isDropOut = dropOutTurn === turn.turn;
    const state = railStateOf(turn, isDropOut);
    const style = RAIL_STATE[state];
    const prev = byTurn.get(turn.turn - 1);

    // `lanes` is a Record keyed by brand name, not a positional list, so sorting and
    // partitioning it is safe. `null` means « cité sans rang lisible », never last.
    const laneEntries = Object.entries(turn.lanes ?? {});
    const ranked = laneEntries
      .filter((e): e is [string, number] => typeof e[1] === 'number')
      .sort((a, b) => a[1] - b[1]);
    const unranked = laneEntries.filter((e) => e[1] === null).map((e) => e[0]);
    const isMine = (name: string) => name.toLowerCase() === brandName.toLowerCase();
    const ownLane = ranked.find(([n]) => isMine(n));
    const ownRank = typeof turn.citedPosition === 'number' ? turn.citedPosition : (ownLane?.[1] ?? null);
    // The denominator is the size of the ENUMERATION the rank was read in, so only the
    // ranked lanes count: an unranked brand in the same list is not a position.
    const rankTotal = Math.max(ranked.length, ownRank ?? 0);

    // `offre` (index 1 of TARGET_CATEGORIES) is indexed but never rendered.
    const cats = (turn.citedCategories ?? []).filter((i) => i !== 1).map((i) => TARGET_CATEGORIES[i]).filter(Boolean);
    const shownMatches = (turn.targetMatches ?? []).filter((m) => m.category !== 'offre');
    const targetCount = new Set(shownMatches.map((m) => m.targetId)).size;

    const pending = turn.analysisStatus === 'pending';

    // ---- rail rows (measured only) ----
    const rows: [string, string, boolean][] = [];
    rows.push(
      ownRank
        ? [t('conversations.trV2.dlRank'), t('conversations.trV2.dlRankValue', { rank: ownRank, total: rankTotal }), false]
        : [t('conversations.trV2.dlRank'), t('conversations.trV2.dlRankNone'), true],
    );
    // `narrativeAlignment: null` is "the answer never discussed the brand", not 0; and a
    // score under « Absent » would be the rail contradicting its own title (2026-09-28).
    if (turn.cited && typeof turn.narrativeAlignment === 'number') {
      rows.push([t('conversations.trV2.dlAlignment'), t('conversations.trV2.dlAlignmentValue', { n: turn.narrativeAlignment }), false]);
    } else if (turn.cited && pending) {
      rows.push([t('conversations.trV2.dlAlignment'), t('conversations.trV2.dlPending'), true]);
    } else if (turn.narrativeAlignment === null || !turn.cited) {
      rows.push([t('conversations.trV2.dlAlignment'), t('conversations.trV2.dlNa'), true]);
    }
    if (typeof turn.competitorCount === 'number') {
      rows.push([t('conversations.trV2.dlCompetitors'), String(turn.competitorCount), turn.competitorCount === 0]);
    }
    rows.push(
      targetCount > 0
        ? [t('conversations.trV2.dlTargets'), String(targetCount), false]
        : [t('conversations.trV2.dlTargets'), t('conversations.trV2.dlNone'), true],
    );

    // ---- observed intent ----
    const intent = turn.observedIntent;
    const depth = depthOf(intent);
    const prevDepth = prev ? depthOf(prev.observedIntent) : null;
    const regressed = depth !== null && prevDepth !== null && depth < prevDepth;
    const intentLabel = intent
      ? t(`conversations.trV2.intent_${intent}`)
      : t('conversations.trV2.intentPending');
    const depthCaption = depth
      ? t(`conversations.trV2.depth_${intent}`)
      : intent
        ? t('conversations.trV2.depthNone')
        : t('conversations.trV2.depthPending');

    const newBrands = [...firstSeen.entries()]
      .filter(([, n]) => n === turn.turn)
      .map(([b]) => b)
      .filter((b) => resolvedDomains?.has(b.toLowerCase()));

    const metaParts: string[] = [];
    if (typeof turn.searchCount === 'number' && turn.searchCount > 0) {
      metaParts.push(t('conversations.trV2.metaSearches', { count: turn.searchCount }));
    }
    metaParts.push(
      sources.length > 0
        ? t('conversations.trV2.metaSources', { count: sources.length })
        : t('conversations.trV2.metaNoSource'),
    );

    return (
      <Box key={`turn-${turn.turn}`} sx={{ ...GRID, position: 'relative', zIndex: 1 }}>
        {/* Gouttière: turn number + observed intent (classifier's label for this turn). */}
        <Box sx={{ gridColumn: { md: '1' }, textAlign: { md: 'right' }, pt: { xs: 2, md: '30px' }, display: 'flex', flexDirection: { xs: 'row', md: 'column' }, alignItems: { xs: 'baseline', md: 'flex-end' }, gap: { xs: 1, md: 0 } }}>
          <Typography component="div" sx={{ fontFamily: TIGHT, fontSize: { xs: '22px', md: '38px' }, lineHeight: 1, fontWeight: 700, letterSpacing: '-0.035em', color: '#DCDAE4', fontVariantNumeric: 'tabular-nums' }}>
            {turn.turn}
          </Typography>
          <Typography component="div" sx={{ ...KICKER_SX, fontSize: { xs: '10px', md: '9.5px' }, mt: { md: '7px' }, lineHeight: 1.25 }}>
            {intentLabel}
          </Typography>
        </Box>

        {/* Inside the screen: the conversation itself, unretouched. */}
        <Box
          id={`turn-${turn.turn}`}
          sx={{
            gridColumn: { md: '2' },
            px: { xs: 0, md: `${INSET}px` },
            pt: { xs: 1.5, md: '30px' },
            pb: { xs: 2, md: '36px' },
            position: 'relative',
            scrollMarginTop: '150px',
            ...(indexInSegment > 0
              ? {
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: { xs: 0, md: `${INSET}px` },
                    right: { xs: 0, md: `${INSET}px` },
                    top: 0,
                    height: '1px',
                    bgcolor: 'divider',
                  },
                }
              : null),
          }}
        >
          {/* The persona's question: a right-aligned bubble, tinted NEUTRAL — violet is « nous ». */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: '24px' }}>
            <Typography
              component="p"
              sx={{
                m: 0,
                maxWidth: '85%',
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '18px 18px 5px 18px',
                p: '13px 17px',
                fontSize: '15.5px',
                lineHeight: 1.5,
                fontWeight: 500,
                letterSpacing: '-0.008em',
                whiteSpace: 'pre-wrap',
              }}
            >
              {turn.personaMessage}
            </Typography>
          </Box>

          <Typography component="p" sx={{ m: '0 0 16px', fontSize: '11.5px', color: 'text.disabled' }}>
            {metaParts.join(' · ')}
          </Typography>

          {/*
           * The engine's answer: full-measure prose, no bubble, no card, no attribution
           * lockup. The white of the screen IS the engine's words — Traaker writes
           * nothing inside it. The serif is reserved to engine output, which is why it
           * alone identifies the voice.
           *
           * MUI typography variants set their own fontFamily/fontSize, so a wrapper's
           * font cannot cascade: the measure is applied through descendant selectors.
           * `[n]` markers scale with it because they are sized in `em`.
           */}
          <Box
            sx={{
              '& .MuiTypography-root': { fontFamily: SERIF, letterSpacing: 0 },
              '& .MuiTypography-body2': { fontSize: '16.5px', lineHeight: 1.7, mb: '15px' },
              '& ol, & ul': { pl: '1.35em', mb: '15px' },
              '& li': { mb: '9px' },
              '& > div > *:last-child': { mb: 0 },
            }}
          >
            <HighlightedResponse
              text={turn.engineResponse ?? ''}
              brandId={brandId}
              sources={sources}
              sourceLinks={turn.sourceLinks}
              targetHighlights={highlights.prose}
              targetSourceHighlights={highlights.sources}
            />
          </Box>

          <TurnSources sources={sources} sourceLinks={turn.sourceLinks} targetMatches={turn.targetMatches} />
        </Box>

        {/*
         * The measurement rail. Its spine is one continuous line the height of the turn:
         * no arrow, no gradient, no direction. The spine says « ensuite », never
         * « plus bas dans le funnel ».
         */}
        <Box
          sx={{
            gridColumn: { md: '3' },
            position: 'relative',
            pl: { md: '24px' },
            pt: { xs: 0, md: '30px' },
            pb: { xs: 2, md: 0 },
            '&::before': {
              content: '""',
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              left: '5px',
              top: '44px',
              ...(isLastOfAll ? { height: '70px' } : { bottom: 0 }),
              ...(style.spine
                ? { width: '3px', borderRadius: '2px', bgcolor: style.spine }
                : { width: 0, borderLeft: '2px dotted', borderColor: 'text.disabled' }),
            },
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', md: 'flex' },
              position: 'absolute',
              left: 0,
              top: '38px',
              width: 13,
              height: 13,
              borderRadius: '50%',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              lineHeight: 1,
              fontWeight: 700,
              color: '#FFFFFF',
              ...(state === 'induced'
                ? { background: 'repeating-linear-gradient(135deg, #A1A1A1 0 2px, #E6E7EB 2px 4px)' }
                : { bgcolor: style.dot }),
              boxShadow: '0 0 0 3.5px #F8F7FC',
            }}
          >
            {style.glyph ?? ''}
          </Box>

          <Typography component="p" sx={{ fontFamily: TIGHT, fontSize: '14.5px', fontWeight: 600, letterSpacing: '-0.015em', m: 0, color: style.ink }}>
            {t(`conversations.trV2.state${STATE_KEY[state]}`)}
            <Box component="small" sx={{ display: 'block', fontFamily: 'inherit', fontWeight: 400, fontSize: '11.5px', color: 'text.secondary', letterSpacing: 0, mt: '3px', lineHeight: 1.4 }}>
              {t(`conversations.trV2.state${STATE_KEY[state]}Sub`)}
            </Box>
          </Typography>

          {state !== 'fail' && <RailRows rows={rows} />}

          {isDropOut && (
            <RailFlag title={t('conversations.annoDropOut')} body={dropReason(turn, prev, t)} />
          )}

          {/*
           * Observed intent — a per-turn card, in its own column, with its own
           * vocabulary. Three rungs of DECREASING width (haut, milieu, bas); the reached
           * rung is violet AND prints its French label, so colour never carries alone.
           * Nothing here is cumulative and nothing points anywhere: a later turn may sit
           * higher than an earlier one, and a regression is marked explicitly below.
           */}
          {state !== 'fail' && (
            <Box
              sx={{
                ...CARD_SX,
                ...(intent === undefined
                  ? { background: 'repeating-linear-gradient(135deg, #FDFDFE 0 5px, #F5F4F9 5px 10px)', borderStyle: 'dashed', borderColor: '#D4D2DC', boxShadow: 'none' }
                  : null),
                ...(regressed
                  ? {
                      // Three marks for ONE fact: the liseré, the ▴ glyph and the
                      // sentence. `info.dark` on purpose, not amber — amber is this
                      // product's weakened / dropped-out colour, and a regression is a
                      // measurement, not a failure. `info` carries no valence.
                      borderColor: 'info.dark',
                      boxShadow: (th) => `inset 3px 0 0 ${th.palette.info.dark}, 0 1px 2px rgba(0, 0, 0, 0.03)`,
                    }
                  : null),
              }}
            >
              <Box sx={KICKER_SX}>{t('conversations.trV2.intentKicker')}</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '8px' }}>
                <Box aria-hidden sx={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: '0 0 30px' }}>
                  {([30, 22, 14] as const).map((w, rung) => {
                    const reached = depth === rung + 1;
                    return (
                      <Box
                        key={w}
                        sx={{
                          width: w,
                          height: reached ? 5 : 3,
                          borderRadius: reached ? '2.5px' : '2px',
                          ...(reached
                            ? { bgcolor: 'primary.main' }
                            : intent === undefined
                              ? { boxShadow: 'inset 0 0 0 1px #D4D2DC' }
                              : { bgcolor: RANK_NEUTRAL }),
                        }}
                      />
                    );
                  })}
                </Box>
                <Box sx={{ fontSize: '13px', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.25, color: depth ? 'text.primary' : 'text.secondary' }}>
                  {intentLabel}
                  <Box component="span" sx={{ display: 'block', fontWeight: 400, fontSize: '11px', color: 'text.disabled', mt: '2px' }}>
                    {depthCaption}
                  </Box>
                </Box>
              </Box>
              {(regressed || intent === undefined || intent === 'other') && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '7px',
                    mt: '10px',
                    pt: '9px',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    fontSize: '11.5px',
                    lineHeight: 1.38,
                    color: regressed ? 'info.dark' : 'text.secondary',
                  }}
                >
                  <Box component="span" aria-hidden sx={{ flex: 'none', lineHeight: 1.3 }}>{regressed ? '▴' : '·'}</Box>
                  <span>
                    {regressed && (
                      <Box component="b" sx={{ display: 'block', color: 'info.dark' }}>{t('conversations.trV2.intentBackTitle')}</Box>
                    )}
                    {regressed
                      ? t('conversations.trV2.intentBackBody', {
                          from: t(`conversations.trV2.depthShort_${prev?.observedIntent}`),
                          to: t(`conversations.trV2.depthShort_${intent}`),
                        })
                      : intent === undefined
                        ? t('conversations.trV2.intentPendingNote')
                        : t('conversations.trV2.intentOtherNote')}
                  </span>
                </Box>
              )}
            </Box>
          )}

          {turn.turnStatus === 'failed' && (
            <RailFlag title={t('conversations.trV2.flagFailedTitle')} body={t('conversations.trV2.flagFailedBody')} />
          )}
          {turn.induced && (
            <RailFlag title={t('conversations.trV2.flagInducedTitle')} body={t('conversations.trV2.flagInducedBody', { brand: brandName })} hatched />
          )}
          {turn.turnStatus !== 'failed' && pending && (
            <RailFlag title={t('conversations.trV2.flagPendingTitle')} body={t('conversations.trV2.flagPendingBody')} />
          )}
          {turn.analysisStatus === 'failed' && (
            <RailFlag title={t('conversations.trV2.flagAnalysisFailedTitle')} body={t('conversations.trV2.flagAnalysisFailedBody')} />
          )}
          {turn.censored && (
            <RailFlag title={t('conversations.trV2.flagCensoredTitle')} body={t('conversations.trV2.flagCensoredBody')} />
          )}

          {/*
           * « Marque non suivie » — only a brand whose NAME resolved to one of this
           * run's cited domains gets a card. The card exists for its « Suivre » button,
           * and a brand we cannot tie to a domain is one we cannot show a logo for or
           * confirm, so it renders nothing here. It stays addable from the « Marques
           * citées » table, which lists every untracked brand with its own button.
           */}
          {newBrands.length > 0 && (
            <Box sx={CARD_SX}>
              <Box sx={KICKER_SX}>{t('conversations.trV2.untrackedKicker')}</Box>
              {newBrands.map((b) => {
                const added = addedBrands.includes(b);
                const resolved = resolvedDomains?.get(b.toLowerCase());
                return (
                  <Box key={b} sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '8px', fontSize: '13px' }}>
                    {resolved && <DomainFavicon domain={resolved.domain} name={resolved.name} size={17} />}
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ fontWeight: 500 }}>{b}</Box>
                      {resolved && (
                        <Box sx={{ color: 'text.disabled', fontSize: '11.5px', overflowWrap: 'anywhere' }}>{resolved.domain}</Box>
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={added || !onAddCompetitor}
                      onClick={() => onAddCompetitor?.(b)}
                      sx={{ ml: 'auto', flex: 'none' }}
                    >
                      {added ? t('conversations.annoUntrackedAdded') : t('conversations.trV2.followShort')}
                    </Button>
                  </Box>
                );
              })}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('conversations.annoUntrackedBody')}
              </Typography>
            </Box>
          )}
        </Box>

        {/*
         * Every signal of the turn, opened in place. The panel is a Traaker sheet laid
         * ON the device and the rail, not screen content — hence the « Mesure Traaker »
         * label and the grey ground, which cannot be mistaken for the engine's words.
         */}
        <Box
          component="details"
          sx={{
            gridColumn: { md: '2 / span 2' },
            position: 'relative',
            zIndex: 3,
            mt: '8px',
            '& > summary': {
              listStyle: 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '7px',
              fontSize: '12.5px',
              fontWeight: 600,
              color: 'primary.dark',
              py: '6px',
            },
            '& > summary::-webkit-details-marker': { display: 'none' },
            '& > summary:hover': { textDecoration: 'underline' },
          }}
        >
          <summary>{t('conversations.trV2.deepSummary', { n: turn.turn })}</summary>
          <Box sx={{ bgcolor: '#F3F2F8', border: '1px solid', borderColor: 'divider', borderRadius: '24px', p: '20px 24px 22px', m: '2px 0 14px', boxShadow: '0 1px 2px rgba(23, 21, 29, 0.05), 0 4px 16px rgba(23, 21, 29, 0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mb: '18px', pb: '13px', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ ...KICKER_SX, color: 'primary.dark' }}>{t('conversations.trV2.deepKicker')}</Box>
              <Box sx={{ fontSize: '12px', color: 'text.secondary' }}>{t('conversations.trV2.deepScope', { n: turn.turn })}</Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: '22px 34px' }}>
              <Box>
                <Box component="h4" sx={{ ...KICKER_SX, m: '0 0 9px' }}>{t('conversations.trV2.deepLanes')}</Box>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', fontSize: '13px' }}>
                  {ranked.map(([name, rank]) => (
                    <Box component="li" key={`r-${name}`} sx={{ display: 'flex', alignItems: 'center', gap: '9px', py: '4.5px', borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box component="span" sx={rankChipSx(rank)}>{rank}</Box>
                      <Box component="span" sx={{ fontWeight: isMine(name) ? 700 : 500, color: isMine(name) ? 'primary.dark' : 'text.primary' }}>{name}</Box>
                      <Box component="span" sx={{ ml: 'auto', fontSize: '11.5px', color: 'text.secondary' }}>
                        {isMine(name)
                          ? t('conversations.trV2.laneMine')
                          : (turn.competitorsMentioned ?? []).includes(name)
                            ? t('conversations.trV2.laneTracked')
                            : t('conversations.trV2.laneUntracked')}
                      </Box>
                    </Box>
                  ))}
                  {unranked.map((name) => (
                    <Box component="li" key={`u-${name}`} sx={{ display: 'flex', alignItems: 'center', gap: '9px', py: '4.5px', borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box component="span" sx={{ ...rankChipSx(99), bgcolor: 'transparent', boxShadow: `inset 0 0 0 1px ${RANK_NEUTRAL}`, color: 'text.secondary' }}>·</Box>
                      <Box component="span" sx={{ fontWeight: isMine(name) ? 700 : 500 }}>{name}</Box>
                      <Box component="span" sx={{ ml: 'auto', fontSize: '11.5px', color: 'text.secondary' }}>{t('conversations.trV2.laneNoRank')}</Box>
                    </Box>
                  ))}
                  {!laneEntries.some(([n]) => isMine(n)) && (
                    <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: '9px', py: '4.5px', borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box component="span" sx={{ ...rankChipSx(99), bgcolor: 'transparent', boxShadow: 'inset 0 0 0 1px #D4D2DC', color: 'text.secondary' }}>—</Box>
                      <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary' }}>{brandName}</Box>
                      <Box component="span" sx={{ ml: 'auto', fontSize: '11.5px', color: 'text.secondary' }}>{t('conversations.trV2.laneAbsent')}</Box>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box>
                <Box component="h4" sx={{ ...KICKER_SX, m: '0 0 9px' }}>{t('conversations.trV2.deepJudge')}</Box>
                <RailRows
                  rows={[
                    turn.cited && typeof turn.narrativeAlignment === 'number'
                      ? [t('conversations.trV2.dlAlignment'), t('conversations.trV2.dlAlignmentValue', { n: turn.narrativeAlignment }), false]
                      : [t('conversations.trV2.dlAlignment'), pending ? t('conversations.trV2.dlPending') : t('conversations.trV2.dlNa'), true],
                    turn.sentiment
                      ? [t('conversations.runSignalNameSentiment'), t(`conversations.trV2.sentiment${turn.sentiment[0].toUpperCase()}${turn.sentiment.slice(1)}`), false]
                      : [t('conversations.runSignalNameSentiment'), pending ? t('conversations.trV2.dlPending') : t('conversations.trV2.dlNa'), true],
                    turn.recommendationStrength
                      ? [t('conversations.runSignalNameRecommendationStrength'), t(`conversations.trV2.strength${turn.recommendationStrength[0].toUpperCase()}${turn.recommendationStrength.slice(1)}`), false]
                      : [t('conversations.runSignalNameRecommendationStrength'), pending ? t('conversations.trV2.dlPending') : t('conversations.trV2.dlNa'), true],
                    [t('conversations.trV2.dlEliminated'), turn.eliminated === true ? t('conversations.trV2.yes') : t('conversations.trV2.no'), turn.eliminated !== true],
                    (turn.otherBrandsCited ?? []).length > 0
                      ? [t('conversations.trV2.dlUntracked'), (turn.otherBrandsCited ?? []).join(', '), false]
                      : [t('conversations.trV2.dlUntracked'), t('conversations.trV2.dlNone'), true],
                  ]}
                />
              </Box>

              <Box>
                <Box component="h4" sx={{ ...KICKER_SX, m: '0 0 9px' }}>{t('conversations.trV2.deepTargets')}</Box>
                {shownMatches.length === 0 ? (
                  <Typography variant="caption" color="text.disabled">{t('conversations.trV2.deepEmpty')}</Typography>
                ) : (
                  <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', fontSize: '13px' }}>
                    {shownMatches.map((m, i) => (
                      <Box component="li" key={`${m.targetId}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '4px', borderTop: '1px solid', borderColor: 'divider' }}>
                        <Box component="span" sx={{ flex: 'none', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'primary.dark', bgcolor: (th) => alpha(th.palette.primary.main, 0.08), borderRadius: '5px', p: '2px 6px' }}>
                          {t(`cibles.tab.${m.category}`)}
                        </Box>
                        <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                          {m.kind === 'pair' ? m.value.slice(0, m.value.lastIndexOf('|')) : m.value}
                        </Box>
                        <Box component="span" sx={{ ml: 'auto', flex: 'none', fontSize: '11px', color: 'text.disabled' }}>
                          {m.via === 'source' ? t('conversations.trV2.viaSource') : t('conversations.trV2.viaText')}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
                {cats.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {cats.map((c) => t(`cibles.tab.${c}`)).join(' · ')}
                  </Typography>
                )}
              </Box>

              <Box>
                <Box component="h4" sx={{ ...KICKER_SX, m: '0 0 9px' }}>{t('conversations.trV2.deepTech')}</Box>
                <RailRows
                  rows={[
                    [t('conversations.trV2.dlSource'), turn.brandIsSource ? t('conversations.trV2.yes') : t('conversations.trV2.no'), !turn.brandIsSource],
                    [t('conversations.trV2.dlSearches'), typeof turn.searchCount === 'number' ? String(turn.searchCount) : t('conversations.trV2.dlNa'), typeof turn.searchCount !== 'number'],
                    [t('conversations.trV2.dlLatency'), t('conversations.trV2.dlLatencyValue', { s: (turn.latencyMs / 1000).toFixed(1) }), false],
                    [t('conversations.trV2.dlLength'), t('conversations.trV2.dlLengthValue', { n: turn.responseTokens }), false],
                    [t('conversations.trV2.dlTruncated'), turn.truncated ? t('conversations.trV2.yes') : t('conversations.trV2.no'), !turn.truncated],
                  ]}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // ---- the device status row (the only chrome, and it is real) ----
  const statusRow = (
    <Box sx={{ ...GRID, position: 'relative', zIndex: 1, pt: { xs: 0, md: '52px' } }}>
      <Box sx={{ display: { xs: 'none', md: 'block' } }} />
      <Box
        sx={{
          gridColumn: { md: '2' },
          mx: { md: `${BEZEL}px` },
          px: { xs: 0, md: `${SCREEN_MARGIN}px` },
          pb: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {engine && (
          <>
            <EngineLogo engine={engine} size={19} />
            <Box sx={{ fontSize: '13.5px', fontWeight: 600, letterSpacing: '-0.012em' }}>
              {ENGINE_META[engineKey(engine)]?.label ?? engine}
            </Box>
          </>
        )}
        {(zoneLabel || countryCode) && (
          <Box sx={{ fontSize: '12px', color: 'text.secondary' }}>
            {countryCode ? `${flagEmoji(countryCode)} ` : ''}{zoneLabel ?? ''}
          </Box>
        )}
        {runDate && (
          <Box sx={{ ml: 'auto', fontSize: '12px', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
            {runDate}
          </Box>
        )}
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'block' } }} />
    </Box>
  );

  const resumeRow = (
    <Box sx={{ ...GRID, position: 'relative', zIndex: 1, pt: { xs: 0, md: '28px' } }}>
      <Box sx={{ display: { xs: 'none', md: 'block' } }} />
      <Box sx={{ gridColumn: { md: '2' }, mx: { md: `${BEZEL}px` }, px: { xs: 0, md: `${SCREEN_MARGIN}px` }, fontSize: '12px', color: 'text.disabled' }}>
        {t('conversations.trV2.resume')}
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'block' } }} />
    </Box>
  );

  // The device foot: a hairline carrying how the conversation ended. `endedBy` lives on
  // the sub-run row, not on ConversationTurn, so it is not reachable through this
  // component's (frozen) props — the sentence states what IS derivable from the turns.
  const devFoot = (
    <Box sx={{ ...GRID, position: 'relative', zIndex: 1, pb: { xs: 2, md: '46px' } }}>
      <Box sx={{ display: { xs: 'none', md: 'block' } }} />
      <Box
        sx={{
          gridColumn: { md: '2' },
          mx: { md: `${BEZEL}px` },
          px: { xs: 0, md: `${SCREEN_MARGIN}px` },
          pt: '16px',
          borderTop: '1px solid',
          borderColor: 'divider',
          fontSize: '12.5px',
          lineHeight: 1.5,
          color: 'text.disabled',
        }}
      >
        <Box component="b" sx={{ color: 'text.secondary', fontWeight: 600 }}>{t('conversations.trV2.footEnd')}</Box>{' '}
        {t('conversations.trV2.footTurns', { count: turns.length })} {t('conversations.transcriptFootnote')}
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'block' } }} />
    </Box>
  );

  // ---- the drop-out band: the only full-bleed element, and it CUTS the frame ----
  const band = (() => {
    if (dropOutTurn === null) return null;
    const turn = byTurn.get(dropOutTurn);
    if (!turn) return null;
    const ahead = Object.entries(turn.lanes ?? {})
      .filter((e): e is [string, number] => typeof e[1] === 'number')
      .filter(([n]) => n.toLowerCase() !== brandName.toLowerCase())
      .sort((a, b) => a[1] - b[1]);
    return (
      <Box
        id="decrochage"
        sx={{
          bgcolor: (th) => alpha(th.palette.error.main, 0.06),
          borderTop: '1px solid',
          borderTopColor: 'error.main',
          borderBottom: '1px solid',
          borderBottomColor: (th) => alpha(th.palette.error.main, 0.22),
        }}
      >
        <Box sx={{ ...GRID, pt: '46px', pb: '44px' }}>
          <Box sx={{ display: { xs: 'none', md: 'block' } }} />
          <Box sx={{ gridColumn: { md: '2 / span 2' } }}>
            <Typography component="p" sx={{ ...KICKER_SX, color: 'error.dark', letterSpacing: '0.1em', fontSize: '11px', m: '0 0 12px' }}>
              {t('conversations.trV2.eventKicker')}
            </Typography>
            <Typography component="h2" sx={{ fontFamily: TIGHT, m: 0, fontSize: { xs: '24px', md: '31px' }, lineHeight: 1.18, letterSpacing: '-0.025em', fontWeight: 700 }}>
              {t('conversations.transcriptDropOut', { brand: brandName })}
            </Typography>
            <Typography component="p" sx={{ m: '14px 0 0', fontSize: '15.5px', lineHeight: 1.55, color: 'text.secondary', maxWidth: '40em' }}>
              {dropReason(turn, byTurn.get(turn.turn - 1), t)}
            </Typography>
            {ahead.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('conversations.trV2.eventNoRanks')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: '26px' }}>
                {ahead.map(([name, rank]) => (
                  <Box key={name} sx={{ bgcolor: 'background.paper', borderRadius: '16px', p: '13px 15px', minWidth: 188, border: '1px solid', borderColor: 'divider', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)', m: '0 10px 10px 0' }}>
                    <Box sx={KICKER_SX}>
                      {rank === 1 ? t('conversations.trV2.eventAheadFirst') : t('conversations.trV2.eventAhead')}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '8px', fontSize: '14px', fontWeight: 600 }}>
                      <Box component="span" sx={{ ...rankChipSx(rank), flex: '0 0 20px', height: 19 }}>{rank}</Box>
                      {name}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  })();

  const lastTurnNumber = turns[turns.length - 1].turn;

  return (
    <Box>
      {/*
       * The two axes, named once, in the order of the columns they live in. `stage` is a
       * bucketing of the turn number; `observedIntent` is measured per turn and can move
       * back up. Different columns, different vocabularies, on purpose.
       */}
      <Box sx={{ ...GRID, pb: 2 }}>
        <Box sx={{ display: { xs: 'none', md: 'block' } }} />
        <Box sx={{ gridColumn: { md: '2 / span 2' }, fontSize: '11.5px', color: 'text.secondary', maxWidth: '56em' }}>
          <Box>{t('conversations.trV2.legendMoment')}</Box>
          <Box sx={{ mt: '4px' }}>{t('conversations.trV2.legendIntent')}</Box>
        </Box>
      </Box>

      {bandAbove && band}

      {head.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          <Chassis edge={sliceAt === null ? 'full' : 'top'} />
          {statusRow}
          {head.map((turn, i) => renderTurn(turn, i, turn.turn === lastTurnNumber))}
          {sliceAt === null && devFoot}
        </Box>
      )}

      {sliceAt !== null && band}

      {tail.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          <Chassis edge="bottom" />
          {resumeRow}
          {tail.map((turn, i) => renderTurn(turn, i, turn.turn === lastTurnNumber))}
          {devFoot}
        </Box>
      )}
    </Box>
  );
}
